import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LeadQuery } from '@troia-v3/types';

import { api } from '@/lib/api';

/**
 * Tipos de operações em lote disponíveis
 */
export type BulkOperation =
  | 'changeStatus'
  | 'changeTemperature'
  | 'changePriority'
  | 'assign'
  | 'changeFunnel'
  | 'delete';

/**
 * Dados necessários para operações em lote
 */
export interface BulkOperationData {
  status?: string;
  temperature?: string;
  priority?: string;
  assigneeId?: string;
  /** changeFunnel: funil de destino (sempre junto de `stepId`). */
  funnelId?: string;
  /** changeFunnel: etapa de destino, que precisa pertencer a `funnelId`. */
  stepId?: string;
}

/**
 * Como o lote escolhe os leads. Exatamente UM dos dois modos por chamada — o
 * backend recusa o payload que traga os dois ou nenhum.
 *
 * - `leadIds` — as linhas marcadas na tela.
 * - `selection` — "todos os que casam com o filtro atual". Manda os MESMOS
 *   `filters`/`search` da listagem e deixa o servidor resolver quem são: com
 *   dezenas de milhares de leads, trafegar os ids seria caro e, pior,
 *   desatualizado (lead novo entra entre a leitura da lista e o clique).
 */
export type BulkSelection =
  | { leadIds: string[]; selection?: never }
  | { leadIds?: never; selection: { filters?: LeadQuery['filters']; search?: string } };

/**
 * Payload completo para operação em lote
 */
export type BulkOperationPayload = BulkSelection & {
  operation: BulkOperation;
  data?: BulkOperationData;
};

/**
 * Response da operação em lote
 */
export interface BulkOperationResponse {
  affected: number;
}

/**
 * Hook para executar operações em lote em leads
 *
 * @example
 * const { mutate: executeBulkOperation, isPending } = useBulkLeadOperations();
 *
 * // seleção da tela
 * executeBulkOperation({ leadIds: ['id1', 'id2'], operation: 'changeStatus', data: { status: 'qualified' } });
 *
 * // todos os que casam com o filtro
 * executeBulkOperation({ selection: { filters, search }, operation: 'delete' });
 */
export const useBulkLeadOperations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BulkOperationPayload): Promise<BulkOperationResponse> => {
      const response = await api.post('/leads/bulk', payload);
      return response.data.data;
    },
    onSuccess: () => {
      // Invalidar cache de leads para atualizar listagens
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      // Bulk delete cascateia soft-delete das atividades — invalidar agenda e views
      // de atividades para não exibir órfãos em cache (idem useDeleteLead).
      queryClient.invalidateQueries({ queryKey: ['calendar-events-range'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['my-activities'] });
    },
  });
};
